/**
 * Platform telestration toolbar — CANVAS_OVERLAY slot.
 *
 * Phase 13 P1 (June 2026)
 *
 * Telestration is the family of post-snap annotation tools coaches
 * use to draw arrows, highlights, and free-form chalk on a scene's
 * canvas. Today these live as a hand-rolled toolbar inside the
 * football play designer; promoting to a platform tool means the
 * Phase 13 P2 whiteboard graduation can adopt the same toolbar
 * without re-implementing it (the binding is by capability —
 * `telestration` — not by scene_type).
 *
 * Tool-mode pattern
 * -----------------
 *
 * The toolbar emits a single `telestration.tool_changed` event on
 * the designer eventBus when the user picks a tool. Canvas
 * overlays (e.g. the football route author) subscribe to that
 * event and switch their pointer behavior. Keeping the toolbar
 * decoupled from any single canvas means it works the same for
 * Konva (2D), Three.js (3D), and the future whiteboard surface.
 */
import React, { useState } from 'react';

import { DesignerTool } from '../DesignerTool';
import { useDesigner } from '../DesignerContext';
import { requiresCapability } from '../predicates';

const TOOLS = [
  { id: 'select',   label: 'Select',  icon: '⌖' },
  { id: 'arrow',    label: 'Arrow',   icon: '→' },
  { id: 'line',     label: 'Line',    icon: '╱' },
  { id: 'free',     label: 'Free',    icon: '✎' },
  { id: 'text',     label: 'Text',    icon: 'T' },
  { id: 'erase',    label: 'Erase',   icon: '⌫' },
];

function TelestrationToolbar() {
  const { eventBus } = useDesigner();
  const [active, setActive] = useState('select');

  const pick = (id) => {
    setActive(id);
    eventBus.emit('telestration.tool_changed', { tool: id });
  };

  return (
    <div
      className="designer-tool designer-tool--canvas-overlay-toolbar"
      data-tool-id="platform.telestration_toolbar"
      role="toolbar"
      aria-label="Telestration tools"
    >
      {TOOLS.map((t) => (
        <button
          type="button"
          key={t.id}
          className={
            'designer-tool__button designer-tool__button--icon' +
            (active === t.id ? ' is-active' : '')
          }
          aria-label={t.label}
          aria-pressed={active === t.id}
          title={t.label}
          onClick={() => pick(t.id)}
        >
          {t.icon}
        </button>
      ))}
    </div>
  );
}

export const telestrationToolbar = new DesignerTool({
  id:         'platform.telestration_toolbar',
  label:      'Telestration',
  slot:       'CANVAS_OVERLAY',
  category:   'annotation',
  applies_to: requiresCapability('telestration'),
  Component:  TelestrationToolbar,
  weight:     50,
  helpText:   'Draw arrows, lines, freehand strokes, and text directly on the scene.',
});

export default telestrationToolbar;
